import React, { useState, useEffect } from 'react';
import { Music, Play, Edit, Trash2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ExamplesSection = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [examples, setExamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    genre: '',
    year: ''
  });

  useEffect(() => {
    // Check if admin is logged in
    const token = localStorage.getItem('admin_token');
    setIsAdmin(!!token);

    const fetchProjects = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/projects`);
        setExamples(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching projects:', err);
        setError('Kunde inte ladda projekt');
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  const getAuthHeader = () => {
    const token = localStorage.getItem('admin_token');
    return token ? { headers: { 'Authorization': `Bearer ${token}` } } : null;
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      title: project.title,
      description: project.description,
      genre: project.genre,
      year: project.year
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm('Är du säker på att du vill ta bort detta projekt?')) return;
    
    const authHeader = getAuthHeader();
    if (!authHeader) {
      toast({
        title: 'Ej inloggad',
        description: 'Du måste vara inloggad för att ta bort projekt.',
        variant: 'destructive'
      });
      return;
    }

    try {
      await axios.delete(`${API}/projects/${projectId}`, authHeader);
      setExamples(examples.filter(p => p.id !== projectId));
      toast({
        title: 'Borttaget!',
        description: 'Projektet har tagits bort.'
      });
    } catch (error) {
      toast({
        title: 'Fel',
        description: 'Kunde inte ta bort projektet.',
        variant: 'destructive'
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const authHeader = getAuthHeader();
    if (!authHeader) {
      toast({
        title: 'Ej inloggad',
        description: 'Du måste vara inloggad för att redigera projekt.',
        variant: 'destructive'
      });
      return;
    }

    try {
      const response = await axios.put(`${API}/projects/${editingProject.id}`, formData, authHeader);
      setExamples(examples.map(p => p.id === editingProject.id ? response.data : p));
      toast({
        title: 'Uppdaterat!',
        description: 'Projektet har uppdaterats.'
      });
      setIsDialogOpen(false);
      setEditingProject(null);
    } catch (error) {
      toast({
        title: 'Fel',
        description: 'Kunde inte uppdatera projektet.',
        variant: 'destructive'
      });
    }
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditingProject(null);
    setFormData({ title: '', description: '', genre: '', year: '' });
  };

  return (
    <section id="exempel" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center space-y-4 mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-black">
            {t('examples.title')}
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {t('examples.subtitle')}
          </p>
          <div className="w-20 h-1 bg-black mx-auto"></div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg">{t('examples.loading')}</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-12">
            <p className="text-red-600 text-lg">{t('examples.error')}</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && examples.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg">{t('examples.empty')}</p>
          </div>
        )}

        {/* Examples Grid */}
        {!loading && !error && examples.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {examples.map((example) => (
            <Card
              key={example.id}
              className="group hover:shadow-xl transition-all duration-300 border-2 border-gray-200 hover:border-black relative"
            >
              {/* Admin Edit/Delete Buttons */}
              {isAdmin && (
                <div className="absolute top-3 right-3 flex space-x-1 z-10">
                  <button
                    onClick={() => handleEdit(example)}
                    className="p-2 bg-white border-2 border-gray-300 rounded-full hover:bg-gray-100 hover:border-black transition-all shadow-sm"
                    title="Redigera"
                  >
                    <Edit className="w-4 h-4 text-black" />
                  </button>
                  <button
                    onClick={() => handleDelete(example.id)}
                    className="p-2 bg-white border-2 border-gray-300 rounded-full hover:bg-red-50 hover:border-red-500 transition-all shadow-sm"
                    title="Ta bort"
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </button>
                </div>
              )}

              <CardHeader>
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-gray-100 rounded-full group-hover:bg-black transition-colors duration-300">
                    <Music className="w-6 h-6 text-black group-hover:text-white transition-colors duration-300" />
                  </div>
                  <button className="p-2 rounded-full bg-black text-white hover:scale-110 transition-transform duration-200">
                    <Play className="w-5 h-5" fill="white" />
                  </button>
                </div>
                <CardTitle className="text-xl font-bold text-black">
                  {example.title}
                </CardTitle>
                <CardDescription className="text-gray-600">
                  {example.description}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between text-sm">
                  <span className="px-3 py-1 bg-gray-100 rounded-full text-gray-700 font-medium">
                    {example.genre}
                  </span>
                  <span className="text-gray-500">{example.year}</span>
                </div>
              </CardContent>
            </Card>
          ))}
          </div>
        )}

        {/* Edit Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Redigera Projekt</DialogTitle>
              <DialogDescription>
                Uppdatera projektinformationen nedan.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="title">Projekttitel</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Beskrivning</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="genre">Genre</Label>
                  <Input
                    id="genre"
                    value={formData.genre}
                    onChange={(e) => setFormData({ ...formData, genre: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="year">År</Label>
                  <Input
                    id="year"
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleDialogClose}
                >
                  Avbryt
                </Button>
                <Button type="submit" className="bg-black text-white hover:bg-gray-800">
                  Uppdatera
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Note about examples */}
        {!loading && !error && examples.length > 0 && (
          <div className="mt-12 text-center">
            <p className="text-gray-500 italic">
              {t('examples.note')}
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default ExamplesSection;